import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDossier } from "./use-dossier";
import { EXAMPLES, FIXTURE_QUERY } from "./fixtures";
import { CandidateCard, EmptyState, PairCard, SkeletonSlot, Slot, formatMs } from "./components";
import { ResultsView } from "./results";
import { CaseReader } from "./case-reader";
import { BasketBar, DetailPane } from "./workspace";
import { type BasketController, type BasketStorage, useBasket } from "./basket";
import type { Dossier, Phase, Ranker, SearchProvider, SearchResult } from "./types";

const BACKEND_LABEL: Record<string, string> = {
  loading: "준비 중",
  mock: "샘플",
  webgpu: "오프라인",
  wasm: "호환 모드",
  remote: "온라인",
};

/** 넓은 워크스페이스 전환 폭. CSS와 같은 값이어야 한다. */
const WIDE_BREAKPOINT = 760;

function phaseText(phase: Phase, scored: number, total: number): string {
  switch (phase) {
    case "searching":
      return "후보 검색 중";
    case "hydrating":
      return `판정 중 ${scored}/${total}`;
    case "done":
      return `판정 완료 ${total}건`;
    case "cancelled":
      return "중단됨";
    case "error":
      return "오류";
    default:
      return "대기 중";
  }
}

function TopBar({ backend }: { backend: string }) {
  return (
    <header className="top">
      <div className="brand">
        <span className="mark" aria-hidden="true">
          法
        </span>
        <span className="brand-name">법통</span>
        <span className="beta">BETA</span>
      </div>
      <div className={`backend backend-${backend}`} aria-label="실행 상태">
        <span className="led" aria-hidden="true" />
        {BACKEND_LABEL[backend] ?? backend}
      </div>
    </header>
  );
}

function QueryBar({
  draft,
  setDraft,
  onSubmit,
  onCancel,
  busy,
}: {
  draft: string;
  setDraft: (value: string) => void;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 116)}px`;
  }, [draft]);

  return (
    <form
      className="query"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(draft);
      }}
    >
      <textarea
        ref={ref}
        value={draft}
        rows={2}
        spellCheck={false}
        placeholder="예: 집주인이 보증금을 돌려주지 않고 갱신도 거절합니다."
        aria-label="검색 질의"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          const isSubmit = event.key === "Enter" && (event.metaKey || event.ctrlKey);
          if (isSubmit && !event.nativeEvent.isComposing) {
            event.preventDefault();
            onSubmit(draft);
          }
        }}
      />
      <div className="query-actions">
        {busy ? (
          <button type="button" className="ghost" onClick={onCancel}>
            중단
          </button>
        ) : (
          <span className="kbd">⌘↵</span>
        )}
        <button type="submit" className="send" disabled={!draft.trim()} aria-label="검색">
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </form>
  );
}

function WideTopBar({
  draft,
  setDraft,
  onSubmit,
  onCancel,
  busy,
  backend,
  basket,
  basketOpen,
  onToggleBasket,
}: {
  draft: string;
  setDraft: (value: string) => void;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  busy: boolean;
  backend: string;
  basket: BasketController;
  basketOpen: boolean;
  onToggleBasket: () => void;
}) {
  return (
    <header className="ws-top">
      <div className="brand">
        <span className="mark" aria-hidden="true">
          法
        </span>
        <span className="brand-name">법통</span>
        <span className="beta">BETA</span>
      </div>
      <form
        className="ws-search"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(draft);
        }}
      >
        <input
          value={draft}
          spellCheck={false}
          placeholder="예: 민법 제750조"
          aria-label="검색 질의"
          onChange={(event) => setDraft(event.target.value)}
        />
        {busy ? (
          <button type="button" className="ghost" onClick={onCancel}>
            중단
          </button>
        ) : (
          <span className="kbd">↵</span>
        )}
        <button type="submit" className="send" disabled={!draft.trim()} aria-label="검색">
          <span aria-hidden="true">→</span>
        </button>
      </form>
      <button
        type="button"
        className="ws-basket-button"
        onClick={onToggleBasket}
        aria-expanded={basketOpen}
        aria-controls="basket-menu"
      >
        인용 <b>{basket.items.length}</b>건
      </button>
      <div className={`backend backend-${backend}`} aria-label="실행 상태">
        <span className="led" aria-hidden="true" />
        {BACKEND_LABEL[backend] ?? backend}
      </div>
    </header>
  );
}

function BasketMenu({
  basket,
  onClose,
}: {
  basket: BasketController;
  onClose: () => void;
}) {
  return (
    <div className="ws-basket-menu" id="basket-menu" role="dialog" aria-label="인용 바구니 목록">
      <div className="ws-basket-menu-head">
        <span>인용 바구니</span>
        <button type="button" className="ws-basket-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
      </div>
      {basket.items.length === 0 ? (
        <p className="ws-basket-empty">아직 담은 인용이 없습니다.</p>
      ) : (
        <ul className="ws-basket-list">
          {basket.items.map((item) => (
            <li key={item.id} className="ws-basket-item">
              <span className="ws-basket-item-main">
                <span className="ws-basket-item-label">{item.citation}</span>
                {item.quote ? (
                  <span className="ws-basket-item-quote">
                    “{item.quote}”
                    {item.quoteLabel ? ` (${item.quoteLabel})` : ""}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className="ws-basket-remove"
                onClick={() => basket.remove(item.id)}
                aria-label={`${item.citation} 빼기`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="ws-basket-menu-foot">
        <button
          type="button"
          className="basket-action basket-action-primary"
          onClick={() => void basket.copy()}
          disabled={basket.items.length === 0}
        >
          {basket.copyState === "done"
            ? "복사됨"
            : basket.copyState === "fail"
              ? "복사 실패"
              : "전체 복사"}
        </button>
        <button
          type="button"
          className="basket-action"
          onClick={basket.clear}
          disabled={basket.items.length === 0}
        >
          비우기
        </button>
      </div>
    </div>
  );
}

function DossierView({
  dossier,
  judgements,
  expanded,
  onToggle,
  loading,
  pending,
}: {
  dossier: Dossier | null;
  judgements: ReturnType<typeof useDossier>["judgements"];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  loading: boolean;
  pending: boolean;
}) {
  if (!dossier) {
    return (
      <div className="dossier dossier-empty">
        <div className="slot slot-wide">
          <header className="slot-head">
            <span className="slot-title">
              <span className="sigil" aria-hidden="true">
                ◇
              </span>
              도시에
            </span>
          </header>
          <div className="slot-body">
            <SkeletonSlot rows={loading ? 5 : 0} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dossier">
      <Slot title="핵심 결속쌍" sigil="◆" count={dossier.pairs.length} className="slot-wide">
        {dossier.pairs.map((pair) => (
          <PairCard
            key={pair.id}
            pair={pair}
            judgements={judgements}
            expanded={expanded.has(pair.id)}
            onToggle={() => onToggle(pair.id)}
            pending={pending}
          />
        ))}
      </Slot>

      <div className="lower">
        <Slot title="관련 법조문" sigil="§" count={dossier.statutes.length}>
          {dossier.statutes.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              judgement={judgements[candidate.id]}
              expanded={expanded.has(candidate.id)}
              onToggle={() => onToggle(candidate.id)}
              pending={pending}
            />
          ))}
        </Slot>

        <Slot title="유사 판례" sigil="¶" count={dossier.cases.length}>
          {dossier.cases.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              judgement={judgements[candidate.id]}
              expanded={expanded.has(candidate.id)}
              onToggle={() => onToggle(candidate.id)}
              pending={pending}
            />
          ))}
        </Slot>
      </div>
    </div>
  );
}

export function App({
  provider,
  ranker,
  notice,
  privacyUrl,
  onOpenSetup,
  basketStorage,
  forceNarrow,
}: {
  provider?: SearchProvider;
  ranker?: Ranker;
  notice?: string;
  privacyUrl?: string;
  /** 판례 전문 볼륨이 없을 때 SetupPanel을 여는 콜백. */
  onOpenSetup?: () => void;
  /** 인용 바구니 저장소. 확장=chrome.storage.local, 데모=localStorage. */
  basketStorage?: BasketStorage;
  /** 프로브/픽스처 경로는 폭과 무관하게 사이드 패널 레이아웃을 쓴다. */
  forceNarrow?: boolean;
} = {}) {
  const {
    phase,
    dossier,
    judgements,
    backend,
    progress,
    timing,
    error,
    stale,
    submit,
    cancel,
  } = useDossier(provider, ranker);

  const showMetrics =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev") === "1";

  const [draft, setDraft] = useState(FIXTURE_QUERY);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reading, setReading] = useState<SearchResult | null>(null);
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [basketOpen, setBasketOpen] = useState(false);
  const autoRan = useRef(false);
  const lastQuery = useRef("");
  const shellRef = useRef<HTMLDivElement>(null);
  const [measuredWide, setMeasuredWide] = useState(false);
  const basket = useBasket(basketStorage);

  const wide = forceNarrow ? false : measuredWide;

  // 폭은 뷰포트가 아니라 앱 컨테이너 기준이다(데모 프레임이 400/820px로 제한한다).
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const update = () => {
      setMeasuredWide(el.getBoundingClientRect().width >= WIDE_BREAKPOINT);
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const run = useCallback(
    (value: string) => {
      lastQuery.current = value;
      setExpanded(new Set());
      setReading(null);
      setSelected(null);
      setBasketOpen(false);
      submit(value);
    },
    [submit],
  );

  useEffect(() => {
    if (autoRan.current) return;
    if (new URLSearchParams(window.location.search).get("demo") === "1") {
      autoRan.current = true;
      run(FIXTURE_QUERY);
    }
  }, [run]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ⌘/Ctrl+Enter: 바구니 복사. 질의 입력창의 ⌘Enter(검색)는 건드리지 않는다.
  const basketRef = useRef(basket);
  basketRef.current = basket;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "TEXTAREA" || target.tagName === "INPUT")) return;
      if (basketRef.current.items.length === 0) return;
      event.preventDefault();
      void basketRef.current.copy();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const busy = phase === "searching" || phase === "hydrating";
  const pending = busy || phase === "idle";
  const showEmpty = !dossier && !busy;
  const results = dossier?.results ?? [];
  const evidenceOnly = dossier?.results !== undefined;
  const basketIds = useMemo(
    () => new Set(basket.items.map((item) => item.id)),
    [basket.items],
  );

  const banners = (
    <>
      {notice ? <div className="banner info">{notice}</div> : null}
      {error ? (
        <div className="banner error">
          <span>요청을 처리하지 못했습니다: {error}</span>
          <button
            type="button"
            className="banner-action"
            onClick={() => run(lastQuery.current || draft)}
          >
            다시 시도
          </button>
        </div>
      ) : null}
      {backend === "wasm" ? (
        <div className="banner warn">호환 모드로 실행 중입니다. 판정이 느릴 수 있습니다.</div>
      ) : null}
    </>
  );

  const foot = (
    <footer className={`foot${wide ? " ws-foot" : ""}`}>
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.scored}
      >
        <span
          style={{
            width: progress.total ? `${(progress.scored / progress.total) * 100}%` : "0%",
          }}
        />
      </div>
      <div className="foot-row">
        <span aria-live="polite">
          {evidenceOnly && phase === "done"
            ? `검색 완료 ${results.length}건`
            : phaseText(phase, progress.scored, progress.total)}
        </span>
        {privacyUrl ? (
          <a className="foot-link" href={privacyUrl} target="_blank" rel="noreferrer">
            개인정보처리방침
          </a>
        ) : null}
        {showMetrics ? (
          <span className="timing">
            {timing.firstBatch !== undefined ? `첫 배지 ${formatMs(timing.firstBatch)} · ` : ""}
            {timing.total !== undefined ? `총 ${formatMs(timing.total)}` : ""}
          </span>
        ) : null}
      </div>
      {backend === "mock" ? (
        <p className="fixture-note">샘플 데이터 · 실제 법령/판례가 아닙니다</p>
      ) : null}
    </footer>
  );

  return (
    <div className="shell" ref={shellRef} data-layout={wide ? "wide" : "narrow"}>
      <main className={`panel phase-${phase}${wide ? " panel-wide" : ""}`}>
        {wide ? (
          <>
            <WideTopBar
              draft={draft}
              setDraft={setDraft}
              onSubmit={run}
              onCancel={cancel}
              busy={busy}
              backend={backend}
              basket={basket}
              basketOpen={basketOpen}
              onToggleBasket={() => setBasketOpen((open) => !open)}
            />
            {banners}
            {basketOpen ? (
              <BasketMenu basket={basket} onClose={() => setBasketOpen(false)} />
            ) : null}

            {showEmpty ? (
              <div className="ws-empty">
                <EmptyState
                  examples={EXAMPLES}
                  onPick={(q) => {
                    setDraft(q);
                    run(q);
                  }}
                />
              </div>
            ) : evidenceOnly ? (
              <div className="ws-body">
                <section className="ws-list" aria-label="검색 결과">
                  <div className={`ws-list-scroll${stale ? " is-stale" : ""}`}>
                    <ResultsView
                      results={results}
                      onSelect={setSelected}
                      selectedPointer={selected?.pointer}
                      basketIds={basketIds}
                      onToggleBasket={basket.toggle}
                    />
                  </div>
                  <BasketBar basket={basket} wide />
                </section>
                <section className="ws-detail" aria-label="상세">
                  <DetailPane
                    result={selected}
                    provider={provider}
                    basket={basket}
                    onOpenSetup={onOpenSetup}
                  />
                </section>
              </div>
            ) : (
              <div className={`ws-single${stale ? " is-stale" : ""}`}>
                <DossierView
                  dossier={dossier}
                  judgements={judgements}
                  expanded={expanded}
                  onToggle={toggle}
                  loading={!dossier}
                  pending={pending}
                />
              </div>
            )}
            {foot}
          </>
        ) : (
          <>
            <TopBar backend={backend} />
            {banners}
            <QueryBar
              draft={draft}
              setDraft={setDraft}
              onSubmit={run}
              onCancel={cancel}
              busy={busy}
            />

            {showEmpty ? (
              <div className="dossier dossier-empty">
                <EmptyState
                  examples={EXAMPLES}
                  onPick={(q) => {
                    setDraft(q);
                    run(q);
                  }}
                />
              </div>
            ) : (
              <div className={`dossier-wrap${stale ? " is-stale" : ""}`}>
                {dossier ? (
                  evidenceOnly ? (
                    <ResultsView
                      results={results}
                      onOpenCase={setReading}
                      basketIds={basketIds}
                      onToggleBasket={basket.toggle}
                    />
                  ) : (
                    <DossierView
                      dossier={dossier}
                      judgements={judgements}
                      expanded={expanded}
                      onToggle={toggle}
                      loading={false}
                      pending={pending}
                    />
                  )
                ) : (
                  <DossierView
                    dossier={null}
                    judgements={judgements}
                    expanded={expanded}
                    onToggle={toggle}
                    loading
                    pending={pending}
                  />
                )}
              </div>
            )}

            {foot}
            <BasketBar basket={basket} wide={false} />

            {reading ? (
              <CaseReader
                result={reading}
                provider={provider}
                onClose={() => setReading(null)}
                onOpenSetup={onOpenSetup}
              />
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
